import { useEffect, useRef, useState } from 'react';
import { useShiftAuth } from '../../contexts/ShiftAuthProvider';
import { isAppleMobile, isShiftStandalone, shiftAppBase } from '../../services/pwa/shift-pwa.utils';
import { shiftPushService } from '../../services/pwa/shift-push.service';
import type { ShiftPushResult } from '../../services/pwa/shift-push.service';

type InstallPrompt = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };

export function useShiftPwaControlsController() {
  const { user, isAuthenticated } = useShiftAuth();
  const [standalone, setStandalone] = useState(isShiftStandalone);
  const [prompt, setPrompt] = useState<InstallPrompt | null>(null);
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ShiftPushResult | null>(null);
  const lock = useRef(false);

  useEffect(() => {
    const capture = (event: Event) => { event.preventDefault(); setPrompt(event as InstallPrompt); };
    const installed = () => { setPrompt(null); setStandalone(isShiftStandalone()); };
    const media = window.matchMedia?.('(display-mode: standalone)');
    window.addEventListener('beforeinstallprompt', capture);
    window.addEventListener('appinstalled', installed);
    media?.addEventListener?.('change', installed);
    return () => {
      window.removeEventListener('beforeinstallprompt', capture);
      window.removeEventListener('appinstalled', installed);
      media?.removeEventListener?.('change', installed);
    };
  }, []);

  useEffect(() => {
    setResult(null);
    if (!isAuthenticated || !standalone || !('Notification' in window) || Notification.permission !== 'granted') return;
    let active = true;
    void Promise.resolve().then(async () => {
      if (!active) return;
      try {
        const next = await shiftPushService.sync();
        if (active) setResult(next);
      } catch {
        if (active) setResult({ status: 'unavailable', message: 'חיבור ההתראות נכשל. ניתן לנסות שוב.' });
      }
    });
    return () => { active = false; };
  }, [isAuthenticated, user?.id, user?.tenantPk, standalone]);

  const onInstall = async () => {
    if (lock.current) return;
    if (isAppleMobile() || !prompt) { setInstructionsOpen(true); return; }
    lock.current = true;
    setBusy(true);
    try {
      await prompt.prompt();
      await prompt.userChoice;
    } catch {
      setInstructionsOpen(true);
    } finally { setPrompt(null); lock.current = false; setBusy(false); }
  };

  const onEnableNotifications = async () => {
    if (lock.current || !isAuthenticated) return;
    lock.current = true;
    setBusy(true);
    try { setResult(await shiftPushService.enable()); }
    catch { setResult({ status: 'unavailable', message: 'חיבור ההתראות נכשל. ניתן לנסות שוב.' }); }
    finally { lock.current = false; setBusy(false); }
  };

  return { standalone, isAuthenticated, busy, result, instructionsOpen, apple: isAppleMobile(), appIcon: `${shiftAppBase}shift-icon-192.png`,
    onInstall, onEnableNotifications, onCloseInstructions: () => setInstructionsOpen(false) };
}